"use client";

import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useCanManageWorkspace } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { updateProject } from "@/app/actions/project/update/action";
import { useFetchProjectOverview } from "@/app/hooks/fetchs/project/useFetchProjectOverview";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PageParams } from "@/app/types/common/DefaultProps";
import { ProjectLite } from "@/app/types/project/ProjectLite";
import { ULogger } from "@/app/utils/ULogger";
import { Limits } from "@/app/vars/Limits";
import BrowserFrame from "./components/BrowserFrame/BrowserFrame";
import ProjectFlow from "./components/ProjectFlow/ProjectFlow";
import styles from "./Overview.module.css";

type Props = {
  params: PageParams;
  project: ProjectLite;
  enabled: boolean;
};

export default function Overview(props: Props) {
  const T = useT();
  const { params, project, enabled } = props;
  const { lang } = params;

  const canEdit = useCanManageWorkspace();
  const { data: stats } = useFetchProjectOverview(project.id, enabled);

  const handleSave = async (field: "description" | "link", value: string) => {
    try {
      const updated = await updateProject(project.id, { [field]: value || undefined });
      if (updated) {
        ProjectStore.getState().updateProject(updated);
      }
    } catch (err) {
      ULogger.error("OverviewTab save failed", err);
    }
  };

  return (
    <BrowserFrame
      link={project.link}
      canEdit={canEdit}
      linkMaxLength={Limits.MAX_LINK}
      onSaveLink={(v) => handleSave("link", v)}
    >
      <div className={styles.page}>
        <EditableText
          value={project.description || ""}
          maxLength={Limits.MAX_PROJECT_DESCRIPTION}
          onSave={(v) => handleSave("description", v)}
          placeholder={T(TKey.PROJECT_DESCRIPTION_PLACEHOLDER) as string}
          multiline
          fitContent
          fullWidthOnEdit
          disabled={!canEdit}
          startEditingOnMount={!project.description && canEdit}
          textClassName={styles.lead}
          inputClassName={styles.lead}
        />

        {stats ? (
          <ProjectFlow lang={lang} stats={stats} />
        ) : (
          <div className={styles.flowLoader}>
            <PointsLoader color="var(--color-green-3)" delay={Limits.CLIENT_AVATAR_LOADER_DELAY} />
          </div>
        )}
      </div>
    </BrowserFrame>
  );
}
